import { useCallback, useEffect, useRef, useState } from 'react';
import { ImageOff } from 'lucide-react';
import { listEventPhotos, PhotosDisabledError, removeEventPhoto } from '../api/eventPhotos';
import type { EventPhoto } from '../api/eventPhotos';
import { audienceCopy } from '../lib/photoAudience';
import type { PhotoUploadAudience } from '../lib/photoAudience';
import { PhotoLightbox } from './PhotoLightbox';
import { PhotoUpload } from './PhotoUpload';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';

/**
 * An event's photographs — TROOP-8, §6; reshaped into a docket panel by TROOP-116.
 *
 * **The same component serves the member's page and the parent's.** `unitId` is undefined on
 * `/family`, which is what picks the guardian's door — never a second set of rules, only a second
 * path (rule 13b). Guardians upload here on the same terms as members, which is the whole point:
 * the parent on the touchline with the camera holds no membership, and a gallery members can fill
 * and families cannot is the wrong way round for the one surface whose purpose is showing families
 * what their children did.
 *
 * **When the unit has photos off, no control renders and the section says why.** Not a disabled
 * button — the design system requires a disabled control to state a reason, and the honest reason
 * here is a leadership decision the viewer cannot act on. So the section explains and offers
 * nothing, which is what "render text saying who does act instead" means.
 *
 * **The same is true when the unit has narrowed who may add** (TROOP-225). Photos are on and the
 * whole gallery is here — narrowing who fills it never narrows who sees it — so the difference is
 * the control and one sentence naming who does add. That sentence matters more than the missing
 * button: a guardian whose classification could not be resolved fails closed and *silently*, and
 * "your unit lets its adults add the photos here" is what turns that into something reportable.
 *
 * **`canAdd` comes from the docket rather than from the gallery's own read.** The event page has
 * already asked, in the one call the resting page is made of, and the answer is per unit rather
 * than per photograph — asking again here would be a second request for a value already on screen.
 *
 * **The grid is bounded.** The dev troop has taught this repeatedly: a screen that renders every row
 * it is given is a bug the database will show you. A campout can carry hundreds of photographs, so
 * the first batch is drawn and the rest sit one tap away rather than being truncated — the same
 * trade `ParticipantStrip` makes, with the true count kept in the docket row above.
 *
 * **What TROOP-116 changed, and why.**
 *
 * The heading is gone: the docket row is the heading, and a panel that restates its own row's label
 * says the same thing twice. It also carried three tokens that do not exist — `--type-title`,
 * `--type-body`, `--type-caption` and `--color-text-danger` — so the heading rendered at inherited
 * body size while every sibling section used `t-subheading`, and the error was not red. A `font:`
 * shorthand naming an undefined custom property is an invalid declaration and is dropped whole,
 * which `tsc` and the build cannot see.
 *
 * The caption is asked **after** files are chosen rather than sitting open above the grid. That
 * flow -- the hidden input, the sheet, the deliberately sequential upload -- moved into
 * `PhotoUpload` when TROOP-119's feed needed the same thing, so this component now owns the grid
 * and nothing about adding to it.
 *
 * Signed URLs expire, and `expiresAt` was returned and never read — a page left open showed broken
 * images. The grid re-lists once when an image fails, which turns that into a refresh.
 */
const FIRST_BATCH = 24;

export function EventGallery({
  unitId,
  eventId,
  zone,
  canAdd,
  uploadAudience,
  onCountChange,
}: {
  /** Undefined on `/family` — that absence is what selects the guardian's door. */
  unitId?: string;
  eventId: string;
  /** The event's zone, so a capture time reads as the trip's clock rather than the viewer's. */
  zone: string;
  /**
   * Whether this reader may add — the server's answer, off the docket (TROOP-225). Never derived
   * here from a permission level: a guardian holds none, and the question reads their classification
   * as well as their position.
   */
  canAdd: boolean;
  /** Who the unit does let add, for the sentence shown when {@link canAdd} is false. */
  uploadAudience: PhotoUploadAudience;
  /** Keeps the docket row's count honest after an upload or a takedown. */
  onCountChange?: (count: number) => void;
}) {
  const [photos, setPhotos] = useState<EventPhoto[] | null>(null);
  const [disabledReason, setDisabledReason] = useState<string | null>(null);

  // The unit narrowed who may add while a sheet was open. A short-lived override on the docket's
  // answer and never a second source of truth for it -- the page's next docket read settles it.
  const [refused, setRefused] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [viewing, setViewing] = useState<number | null>(null);

  // One refresh per mount, so a genuinely broken image cannot spin the endpoint.
  const refreshed = useRef(false);

  const load = useCallback(async () => {
    try {
      setDisabledReason(null);
      // The docket's `canAdd` is what settles this, and a fresh docket read arrives as a new prop
      // rather than through here -- but the read below is the closest thing this component has to
      // "something changed", so a unit that widened its audience again gets the control back
      // without the reader having to leave the page.
      setRefused(false);
      const loaded = await listEventPhotos(unitId, eventId);
      setPhotos(loaded);
      if (loaded) onCountChange?.(loaded.length);
    } catch (e) {
      if (e instanceof PhotosDisabledError) {
        setDisabledReason(e.message);
        setPhotos([]);
        return;
      }
      setError(e instanceof Error ? e.message : 'The gallery could not be loaded.');
    }
  }, [unitId, eventId, onCountChange]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onRemove(photo: EventPhoto) {
    setBusy(true);
    setError(null);

    try {
      await removeEventPhoto(unitId, eventId, photo.id);
      setViewing(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That photo could not be removed.');
    } finally {
      setBusy(false);
    }
  }

  // Null means no such event, or none this caller may read. The event page has already said so.
  if (photos === null) return null;

  const shown = showAll ? photos : photos.slice(0, FIRST_BATCH);
  const hidden = photos.length - shown.length;

  const mayAdd = canAdd && !refused;
  const audience = audienceCopy(uploadAudience);

  return (
    <div className="flex flex-col" style={{ gap: 'var(--space-4)' }}>
      {disabledReason ? (
        // No control at all, and a sentence naming the decision. See the component remarks.
        <p className="t-body" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
          {/* Named accurately since TROOP-225 built the screen this points at, and named as a
              person rather than as a destination: this component also renders on the /family
              docket, where the reader 404s on Manage along with every other /units/ route. A
              sentence pointing somewhere half its audience cannot reach is the older half of this
              bug in a new place. */}
          {disabledReason} Your unit&rsquo;s adult leaders can turn them back on.
        </p>
      ) : mayAdd ? (
        <div>
          {/* The control, the caption sheet and the sequential upload all live in PhotoUpload --
              the photo feed needs the same flow, and a second copy is how the two would drift
              (TROOP-119). No event title here: there is only one event this could mean. */}
          <PhotoUpload
            unitId={unitId}
            eventId={eventId}
            onAdded={load}
            onDisabled={setDisabledReason}
            onRefused={() => setRefused(true)}
          />
        </div>
      ) : (
        // No control, and a sentence naming who does add (TROOP-225). Not a disabled button: the
        // design system requires one to state a reason, and "your unit chose somebody else" is not
        // a reason this reader can act on. The photographs below are unaffected.
        <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
          {audience.whoDoes}
        </p>
      )}

      {error && (
        <p
          className="t-caption"
          role="alert"
          style={{ color: 'var(--color-status-blocked)', margin: 0 }}
        >
          {error}
        </p>
      )}

      {photos.length === 0 && !disabledReason && (
        <EmptyState
          icon={ImageOff}
          headline="No photos yet"
          // The unit's own answer, not a literal. This read "Anyone in the unit, and any parent,
          // can add them" until TROOP-225 made that a claim a unit can have withdrawn -- prose on
          // a screen goes stale exactly the way a count does, and no test can tell you.
          //
          // Only for somebody who may add. A reader who may not has the same sentence a few
          // pixels above, where the control would have been, and saying it twice on one empty
          // screen reads as a stutter rather than as emphasis.
          explanation={mayAdd ? audience.whoDoes : 'Nobody has added any yet.'}
        />
      )}

      {shown.length > 0 && (
        <ul
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: 'var(--space-3)',
            listStyle: 'none',
            padding: 0,
            margin: 0,
          }}
        >
          {shown.map((photo, i) => (
            <li key={photo.id}>
              <button
                type="button"
                onClick={() => setViewing(i)}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: 0,
                  // No border. A 1px rule around a photograph is fussy at this size and fights the
                  // picture; the grid gap is what separates them.
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  overflow: 'hidden',
                  background: 'var(--color-surface-sunken)',
                  cursor: 'pointer',
                }}
              >
                <img
                  src={photo.thumbnailUrl ?? photo.url}
                  alt={photo.caption ?? `Photo added by ${photo.addedByDisplayName}`}
                  loading="lazy"
                  onError={() => {
                    // A signed URL that has expired, most likely. One re-list per mount turns a
                    // page left open overnight into a refresh rather than a wall of broken images.
                    if (refreshed.current) return;
                    refreshed.current = true;
                    void load();
                  }}
                  style={{ display: 'block', width: '100%', aspectRatio: '1', objectFit: 'cover' }}
                />
              </button>
            </li>
          ))}
        </ul>
      )}

      {hidden > 0 && (
        <div>
          <Button variant="secondary" onClick={() => setShowAll(true)}>
            Show {hidden} more
          </Button>
        </div>
      )}

      {viewing !== null && (
        <PhotoLightbox
          photos={photos}
          index={viewing}
          zone={zone}
          busy={busy}
          onIndex={setViewing}
          onClose={() => setViewing(null)}
          onRemove={(p) => void onRemove(p)}
        />
      )}
    </div>
  );
}
